import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DataGrid from 'components/common/data-grid/data-grid-component';
import { DataGridDataApi } from 'models/utils/common/data-grid/data-grid-data-model';
import {
  getEngagementListColumns,
  OMNIA_ENGAGEMENT_LIST_BLOCK,
  OMNIA_ENGAGEMENT_LIST_ID_BLOCK,
  OMNIA_LINKING_UNLINKING_PROJECT_ID,
} from 'constants/feature/omnia-engagements-constants';
import { connect } from 'react-redux';
import {
  handleProjectClick,
  handleOmniaEngagementLinking,
  handleOmniaEngagementUnlinking,
} from 'store/actions/omnia-engagement-actions';
import { setSelectedProjectFromProjectList } from 'store/actions/selected-project-actions';
import { fetchProjectList } from 'store/actions/project-list-actions';
import OmniaEngagement from 'models/api/omnia-engagement-model';
import OmniaLinkingConfirmModal from './omnia-linking-confirmation-modal';
import OmniaUnlinkingConfirmModal from './omnia-unlinking-confirmation-modal';
import OmniaEngagementEntry from 'models/data/omnia-engagement-entry-model';
import { useCallback } from 'react';
import { isNullOrUndefined } from 'utils/object-utils';

const OmniaEngagementList = ({
  omniaEngagement,
  handleProjectClick,
  fetchProjectList,
  handleOmniaEngagementLinking,
  handleOmniaEngagementUnlinking,
  setSelectedProjectFromProjectList,
  stateData,
}) => {
  useEffect(() => {
    fetchProjectList();
  }, [fetchProjectList]);

  const [showLinkConfirmModal, setShowLinkConfirmModal] = useState(false);
  const [showUnlinkConfirmModal, setShowUnlinkConfirmModal] = useState(false);
  const [engagementToLink, setEngagementToLink] = useState(
    new OmniaEngagementEntry(),
  );

  const handleOnClickLink = useCallback((engagement) => {
    localStorage.setItem(
      OMNIA_LINKING_UNLINKING_PROJECT_ID,
      engagement.toutProjectId,
    );
    setShowLinkConfirmModal(true);
    setEngagementToLink(engagement);
  }, []);

  const handleOnClickUnlink = useCallback((engagement) => {
    localStorage.setItem(
      OMNIA_LINKING_UNLINKING_PROJECT_ID,
      engagement.toutProjectId,
    );
    setShowUnlinkConfirmModal(true);
    setEngagementToLink(engagement);
  }, []);

  const setSelectedProjectByomniaEngagement = (engagement) => {
    const allProjects = stateData.projectList.getProjects();
    const projectEntry = allProjects.find(
      (project) => project.id === engagement.toutProjectId,
    );
    setSelectedProjectFromProjectList({ projectListEntry: projectEntry });
  };

  return (
    <div className={OMNIA_ENGAGEMENT_LIST_BLOCK}>
      <DataGrid
        tableId={`${OMNIA_ENGAGEMENT_LIST_ID_BLOCK}-list`}
        className={`${OMNIA_ENGAGEMENT_LIST_BLOCK}__list`}
        columns={getEngagementListColumns({
          onProjectClick: handleProjectClick,
          handleOnClickLink,
          handleOnClickUnlink,
          omniaEngagement,
          stateData,
        })}
        dataModel={
          new DataGridDataApi({
            apiModel: omniaEngagement,
            rowItems: omniaEngagement.omniaTieoutEngagementEntityList,
          })
        }
      />
      {showLinkConfirmModal && (
        <OmniaLinkingConfirmModal
          omniaEngagement={omniaEngagement}
          engagementToLink={engagementToLink}
          onClickCancel={() => {
            setShowLinkConfirmModal(false);
            localStorage.removeItem(OMNIA_LINKING_UNLINKING_PROJECT_ID);
          }}
          onClickLink={() => {
            if (!isNullOrUndefined(omniaEngagement.getGeoName())) {
              setShowLinkConfirmModal(false);
              handleOmniaEngagementLinking({
                engagement: engagementToLink,
                omEngagementId: omniaEngagement.getEngagementId(),
                geoCode: omniaEngagement.getGeoName(),
              });
              setSelectedProjectByomniaEngagement(engagementToLink);
              localStorage.removeItem(OMNIA_LINKING_UNLINKING_PROJECT_ID);
            }
          }}
        />
      )}
      {showUnlinkConfirmModal && (
        <OmniaUnlinkingConfirmModal
          omniaEngagement={omniaEngagement}
          engagementToUnlink={engagementToLink}
          onClickCancel={() => {
            setShowUnlinkConfirmModal(false);
            localStorage.removeItem(OMNIA_LINKING_UNLINKING_PROJECT_ID);
          }}
          onClickUnlink={(engagementToUnlink) => {
            if (!isNullOrUndefined(omniaEngagement.getGeoName())) {
              setShowUnlinkConfirmModal(false);
              handleOmniaEngagementUnlinking({
                engagementToUnlink,
                geoCode: omniaEngagement.getGeoName(),
              });
              setSelectedProjectByomniaEngagement(engagementToUnlink);
              localStorage.removeItem(OMNIA_LINKING_UNLINKING_PROJECT_ID);
            }
          }}
        />
      )}
    </div>
  );
};

OmniaEngagementList.propTypes = {
  /** Omnia engagement object  */
  omniaEngagement: PropTypes.instanceOf(OmniaEngagement).isRequired,
  /** Action to handle when a project is clicked in the engagment project list */
  handleProjectClick: PropTypes.func.isRequired,
  /** Action for linking omnia engagements */
  handleOmniaEngagementLinking: PropTypes.func.isRequired,
  /** Action for unlinking omnia engagements */
  handleOmniaEngagementUnlinking: PropTypes.func.isRequired,
  /** Action to fetch the list of projects */
  fetchProjectList: PropTypes.func.isRequired,
};

const mapStateToProps = ({ data }) => ({ stateData: data });

const mapDispatchToProps = {
  handleProjectClick,
  fetchProjectList,
  handleOmniaEngagementLinking,
  handleOmniaEngagementUnlinking,
  setSelectedProjectFromProjectList,
};

export { OmniaEngagementList };

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OmniaEngagementList);
